import { CommunityPayload, NearbyDistance } from "../payloadTypes";
import { CommunityData, FactRow } from "../types";
import {
  isFiniteInRange,
  toTrimmedString,
  warnOmitted,
  withHeroImageParams,
} from "./neighbourhoodHelpers";
import config from "@Config/index";

const KEY_PREFIX = "propertypage.components.neighbourhood.community";

const heroImage = withHeroImageParams(
  `${config.IMAGE_STATIC_URL}/neighbourhood-assets/community.png`,
);

const CITY_CENTER = "city center";
const DISTANCE_PATTERN = /^(\d+(?:\.\d+)?)\s*([A-Za-z]+)$/;

interface ParsedDistance {
  /** The numeric portion, used for comparison only. */
  value: number;
  /** Lower-cased unit token, used for the mixed-unit check only. */
  unitKey: string;
  /** The trimmed original string, rendered verbatim. */
  display: string;
}

/** Universities_List: every nearby distance except the city-centre entry. */
export const selectUniversities = (raw: unknown): NearbyDistance[] =>
  Array.isArray(raw)
    ? (raw as NearbyDistance[]).filter(
        (entry) => toTrimmedString(entry?.place)?.toLowerCase() !== CITY_CENTER,
      )
    : [];

/** Parses a NearbyDistance entry into its numeric, unit, and display parts. */
export const parseDistance = (entry: NearbyDistance): ParsedDistance | undefined => {
  const trimmed = toTrimmedString(entry?.distance);
  if (!trimmed) return undefined;
  const match = DISTANCE_PATTERN.exec(trimmed);
  if (!match) return undefined;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return undefined;
  return { value, unitKey: match[2].toLowerCase(), display: trimmed };
};

/**
 * Derives the universities Fact_Row from the parsed entries.
 * Returns undefined when the row should be omitted.
 */
const buildUniversitiesRow = (universities: NearbyDistance[]): FactRow | undefined => {
  const parsed = universities
    .map(parseDistance)
    .filter((p): p is ParsedDistance => p !== undefined);

  if (parsed.length === 0) return undefined;

  const units = parsed.reduce<Set<string>>((acc, p) => acc.add(p.unitKey), new Set<string>());
  if (units.size > 1) {
    warnOmitted("universities fact row", "nearby_distances (mixed units)");
    return undefined;
  }

  // Largest numeric portion; ties keep the first occurrence (deterministic).
  const furthest = parsed.reduce((best, p) => (p.value > best.value ? p : best));

  return {
    key: `${KEY_PREFIX}.fact.universities`,
    params: { count: parsed.length, distance: furthest.display },
    trend: "neutral",
  };
};

/**
 * Transforms community data into CommunityData for display.
 * Rows in order: median age, universities, renters, noise.
 * Every row trend is neutral.
 */
/**
 * Demographics source varies by country:
 *   US → US Census ACS,  UK → Nomis/NRS/NISRA,  else → generic census demographics.
 */
const resolveSourceKey = (countryCode?: string): string => {
  const cc = countryCode?.trim().toUpperCase();
  if (cc === "US") return `${KEY_PREFIX}.source.us`;
  if (cc === "GB" || cc === "UK") return `${KEY_PREFIX}.source.uk`;
  return `${KEY_PREFIX}.source`;
};

export const transformCommunity = (
  community: CommunityPayload | null | undefined,
  universities: NearbyDistance[],
  countryCode?: string,
): CommunityData => {
  const facts: FactRow[] = [];

  // --- Median age row ---
  const medianAge = community?.medianAge;
  if (isFiniteInRange(medianAge, 0, 120)) {
    facts.push({
      key: `${KEY_PREFIX}.fact.medianAge`,
      params: { medianAge: Math.round(medianAge) },
      trend: "neutral",
    });
  } else if (medianAge !== null && medianAge !== undefined) {
    warnOmitted("medianAge fact row", "community.medianAge");
  }

  // --- Universities row ---
  const universitiesRow = buildUniversitiesRow(universities);
  if (universitiesRow) {
    facts.push(universitiesRow);
  }

  // --- Renters row ---
  const renterPercentage = community?.renterPercentage;
  if (isFiniteInRange(renterPercentage, 0, 100)) {
    facts.push({
      key: `${KEY_PREFIX}.fact.renters`,
      params: { renterPercentage: Math.round(renterPercentage) },
      trend: "neutral",
    });
  } else if (renterPercentage !== null && renterPercentage !== undefined) {
    warnOmitted("renters fact row", "community.renterPercentage");
  }

  // --- Noise level (banner, not a fact row) ---
  // Negative signal (P1D-5482 FR-2): noise higher than the comparing city is
  // hidden, so only the "lower" banner is ever built.
  const noiseDiff = community?.noise_percent_difference;
  let noiseLevel: "higher" | "lower" | undefined;
  if (typeof noiseDiff === "number" && Number.isFinite(noiseDiff)) {
    if (noiseDiff < 0) {
      noiseLevel = "lower";
    } else if (noiseDiff > 0) {
      warnOmitted("noise banner", "community.noise_percent_difference (negative signal)");
    }
  }

  // --- Hero fallback chain ---
  const studentPop = community?.studentPopulationPercentage;
  const renterPop = community?.renterPercentage;

  let mainStat: string | undefined;
  let statLabelKey: string | undefined;
  let statKind: "student" | "renter" | undefined;

  if (isFiniteInRange(studentPop, 0, 100)) {
    mainStat = `${Math.round(studentPop)}%`;
    statLabelKey = `${KEY_PREFIX}.hero.label.student`;
    statKind = "student";
  } else if (isFiniteInRange(renterPop, 0, 100)) {
    mainStat = `${Math.round(renterPop)}%`;
    statLabelKey = `${KEY_PREFIX}.hero.label.renter`;
    statKind = "renter";
  }

  return {
    heroImage,
    ...(mainStat !== undefined && { mainStat }),
    ...(statLabelKey !== undefined && { statLabelKey }),
    ...(statKind !== undefined && { statKind }),
    ...(noiseLevel !== undefined && { noiseLevel }),
    facts,
    sourceKey: resolveSourceKey(countryCode),
  };
};

export default transformCommunity;
